import React, { useState, useEffect, useCallback, useRef } from 'react';

const API = '/api';

// ── Thème clair ───────────────────────────────────────────────────────────────
const C = {
  bg:       '#f1f5f9',
  surface:  '#ffffff',
  card:     '#ffffff',
  border:   '#e2e8f0',
  accent:   '#2563eb',
  accentHi: '#1d4ed8',
  accentBg: '#eff6ff',
  green:    '#059669',
  greenBg:  '#ecfdf5',
  amber:    '#d97706',
  amberBg:  '#fffbeb',
  red:      '#dc2626',
  redBg:    '#fef2f2',
  muted:    '#94a3b8',
  mutedBg:  '#f8fafc',
  text:     '#0f172a',
  textSub:  '#475569',
  textMut:  '#94a3b8',
  purple:   '#7c3aed',
  purpleBg: '#f5f3ff',
  shadow:   '0 1px 3px rgba(0,0,0,.08), 0 1px 2px rgba(0,0,0,.06)',
  shadowMd: '0 4px 12px rgba(0,0,0,.10)',
};

const PRIORITY_COLOR = { 'Très fort': C.green,  'Fort': C.accent,  'Moyen': C.amber,  'Faible': C.muted };
const PRIORITY_BG    = { 'Très fort': C.greenBg, 'Fort': C.accentBg,'Moyen': C.amberBg,'Faible': C.mutedBg };
const STATUS_COLOR   = { 'À vérifier': C.amber, 'À contacter': C.accent, 'Contacté': C.green, 'Répondu': C.purple, 'Non pertinent': C.muted };
const STATUS_BG      = { 'À vérifier': C.amberBg,'À contacter': C.accentBg,'Contacté': C.greenBg,'Répondu': C.purpleBg,'Non pertinent': C.mutedBg };
const ACTION_ICONS   = { 'appel': '📞', 'email': '📧', 'linkedin': '💼', 'réunion': '🤝', 'relance': '🔁', 'note': '📝' };

const TYPES    = ['Indépendant', 'Chaîne', 'Groupement', 'Verrier', 'Distributeur'];
const SOURCES  = ['Site web', 'LinkedIn', 'Acuité', 'Presse', 'Salon', 'Autre'];
const STATUSES = ['À vérifier', 'À contacter', 'Contacté', 'Répondu', 'Non pertinent'];
const PRIORITIES   = ['Très fort', 'Fort', 'Moyen', 'Faible'];
const ACTION_TYPES = ['appel', 'email', 'linkedin', 'réunion', 'relance', 'note'];
const KNOWN_COUNTRIES = ['France', 'Espagne', 'Italie', 'Allemagne', 'Japon', 'Belgique', 'Suisse', 'Royaume-Uni', 'Canada', 'USA', 'Tunisie', 'Maroc', 'Algérie', 'International'];
const KNOWN_TYPES     = ['Chaîne', 'Indépendant', 'Groupement', 'Distributeur', 'Verrier'];
const TAG_PALETTE = ['#2563eb','#059669','#d97706','#dc2626','#7c3aed','#0891b2','#db2777','#65a30d'];

const SOURCE_CATEGORIES = {
  'Site web': { icon: '🌐', match: (s) => !['LinkedIn','Acuité','Presse','Salon','Autre'].includes(s) || s === 'Site web' },
  'LinkedIn': { icon: '💼', match: (s) => s === 'LinkedIn' },
  'Acuité':   { icon: '📰', match: (s) => s === 'Acuité' },
  'Presse':   { icon: '🗞', match: (s) => s === 'Presse' },
  'Salon':    { icon: '🎪', match: (s) => s === 'Salon' },
  'Autre':    { icon: '📌', match: (s) => s === 'Autre' },
};

const G = {
  card: { background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: '20px 24px', boxShadow: C.shadow },
  btn: { background: C.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '9px 18px', cursor: 'pointer', fontSize: 14, fontWeight: 600, fontFamily: 'inherit' },
  btnGhost: { background: '#fff', color: C.textSub, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontSize: 14, fontFamily: 'inherit' },
  input: { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '9px 14px', fontSize: 14, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box', outline: 'none', boxShadow: '0 1px 2px rgba(0,0,0,.04)' },
  select: { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, padding: '9px 14px', fontSize: 14, fontFamily: 'inherit', cursor: 'pointer', outline: 'none' },
  label: { fontSize: 11, color: C.textSub, fontWeight: 600, display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.07em' },
  tag: (color, bg) => ({ background: bg || color+'18', color, border: `1px solid ${color}30`, borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap' }),
};

// Formate une date ISO en heure locale du navigateur
function _fmtDate(iso, opts = {}) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
      ...opts
    });
  } catch { return iso; }
}
function _fmtDateShort(iso) { return _fmtDate(iso, { year: undefined }); }
function _fmtDateOnly(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('fr-FR'); }
  catch { return iso.slice(0, 10); }
}

function api(path, opts = {}) {
  const token = localStorage.getItem('ld_token');
  return fetch(API + path, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) },
    body: opts.body ? JSON.stringify(opts.body) : undefined,
  }).then(r => { if (!r.ok) return r.json().then(e => Promise.reject(e.detail || 'Erreur')); return r.json(); });
}

function _normalizeCountry(c) { if (!c) return 'International'; return KNOWN_COUNTRIES.includes(c.trim()) ? c.trim() : 'International'; }
function _normalizeType(t) { if (!t) return 'Autre'; return KNOWN_TYPES.includes(t.trim()) ? t.trim() : 'Autre'; }
function _sourceLabel(lead) {
  if (lead.publication_url) { try { return new URL(lead.publication_url).hostname.replace('www.',''); } catch {} }
  return lead.source || 'Autre';
}

// ── Tags personnalisés ───────────────────────────────────────────────────────
function TagBadge({ tag, onRemove }) {
  const color = TAG_PALETTE[Math.abs(tag.split('').reduce((a, c) => a + c.charCodeAt(0), 0)) % TAG_PALETTE.length];
  return (
    <span style={{ ...G.tag(color), display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px' }}>
      {tag}
      {onRemove && (
        <button onClick={e => { e.stopPropagation(); onRemove(tag); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color, padding: 0, lineHeight: 1, fontSize: 11, opacity: .7 }}>✕</button>
      )}
    </span>
  );
}

function TagEditor({ leadId, currentTags = [], onUpdate }) {
  const [input, setInput] = useState('');
  const [tags, setTags] = useState(currentTags);
  const [allTags, setAllTags] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Charger tous les tags existants depuis les leads
  useEffect(() => {
    api('/leads').then(leads => {
      const set = new Set();
      leads.forEach(l => (l.tags || []).forEach(t => set.add(t)));
      setAllTags([...set].sort());
    }).catch(() => {});
  }, []);

  const add = async (tag) => {
    const t = tag.trim().toLowerCase();
    if (!t || tags.includes(t)) return;
    const next = [...tags, t];
    setTags(next);
    setInput('');
    setShowSuggestions(false);
    await onUpdate(leadId, { tags: next });
  };

  const remove = async (tag) => {
    const next = tags.filter(t => t !== tag);
    setTags(next);
    await onUpdate(leadId, { tags: next });
  };

  const suggestions = allTags.filter(t => t.includes(input.toLowerCase()) && !tags.includes(t)).slice(0, 6);

  return (
    <div style={{ marginTop: 12 }}>
      <div style={G.label}>🏷 Tags</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
        {tags.map(t => <TagBadge key={t} tag={t} onRemove={remove} />)}
        {tags.length === 0 && <span style={{ fontSize: 12, color: C.muted }}>Aucun tag</span>}
      </div>
      <div style={{ position: 'relative' }}>
        <input
          value={input}
          onChange={e => { setInput(e.target.value); setShowSuggestions(true); }}
          onKeyDown={e => { if (e.key === 'Enter' && input.trim()) add(input); if (e.key === 'Escape') setShowSuggestions(false); }}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
          placeholder="Ajouter un tag… (ex: SILMO 2025, VIP, Suivi Q3)"
          style={{ ...G.input, fontSize: 13 }}
        />
        {showSuggestions && (input || allTags.length > 0) && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: `1px solid ${C.border}`, borderRadius: 8, boxShadow: C.shadowMd, zIndex: 100, marginTop: 4 }}>
            {suggestions.length === 0 && input && (
              <div onClick={() => add(input)} style={{ padding: '8px 14px', fontSize: 13, color: C.accent, cursor: 'pointer' }}>
                + Créer le tag "{input}"
              </div>
            )}
            {suggestions.map(t => (
              <div key={t} onClick={() => add(t)} style={{ padding: '8px 14px', fontSize: 13, color: C.text, cursor: 'pointer', borderBottom: `1px solid ${C.border}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.bg}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                <TagBadge tag={t} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Recherche globale ─────────────────────────────────────────────────────────
function GlobalSearch({ onNavigate }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handleKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') { e.preventDefault(); setOpen(true); ref.current?.focus(); }
      if (e.key === 'Escape') { setOpen(false); setQuery(''); setResults([]); }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const search = (q) => {
    if (!q.trim()) { setResults([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const leads = await api('/leads');
        const ql = q.toLowerCase();
        const matched = leads.filter(l =>
          l.company?.toLowerCase().includes(ql) ||
          l.publication_text?.toLowerCase().includes(ql) ||
          l.notes?.toLowerCase().includes(ql) ||
          l.contact_name?.toLowerCase().includes(ql) ||
          l.contact_email?.toLowerCase().includes(ql) ||
          l.city?.toLowerCase().includes(ql) ||
          l.country?.toLowerCase().includes(ql) ||
          (l.tags || []).some(t => t.includes(ql)) ||
          l.gemini_analysis?.resume?.toLowerCase().includes(ql) ||
          l.gemini_analysis?.signal_detecte?.toLowerCase().includes(ql)
        ).slice(0, 8);
        setResults(matched);
      } catch {}
      setLoading(false);
    }, 250);
  };

  const handleSelect = (lead) => {
    onNavigate('leads', lead.id);
    setOpen(false); setQuery(''); setResults([]);
  };

  const scoreColor = (s) => s >= 80 ? C.green : s >= 60 ? C.accent : s >= 40 ? C.amber : C.muted;

  if (!open) return (
    <button onClick={() => setOpen(true)} style={{ ...G.btnGhost, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '7px 14px', color: C.textSub }}>
      <span>🔍</span>
      <span>Recherche globale</span>
      <span style={{ fontSize: 11, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 4, padding: '1px 6px', marginLeft: 4 }}>Ctrl+K</span>
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.4)', zIndex: 500, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 80 }}
      onClick={() => { setOpen(false); setQuery(''); setResults([]); }}>
      <div style={{ width: '100%', maxWidth: 640, background: '#fff', borderRadius: 16, boxShadow: '0 20px 60px rgba(0,0,0,.2)', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        {/* Input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 18 }}>🔍</span>
          <input
            ref={ref}
            autoFocus
            value={query}
            onChange={e => { setQuery(e.target.value); search(e.target.value); }}
            placeholder="Chercher une entreprise, un tag, un contact, une ville…"
            style={{ ...G.input, border: 'none', boxShadow: 'none', fontSize: 16, padding: 0 }}
          />
          {loading && <span style={{ fontSize: 12, color: C.muted }}>…</span>}
          <button onClick={() => { setOpen(false); setQuery(''); setResults([]); }} style={{ ...G.btnGhost, padding: '4px 10px', fontSize: 12 }}>Esc</button>
        </div>

        {/* Résultats */}
        {results.length > 0 && (
          <div style={{ maxHeight: 480, overflowY: 'auto' }}>
            {results.map((lead, i) => (
              <div key={lead.id} onClick={() => handleSelect(lead)}
                style={{ padding: '12px 20px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, display: 'flex', gap: 14, alignItems: 'center' }}
                onMouseEnter={e => e.currentTarget.style.background = C.bg}
                onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: C.text }}>{lead.company || 'Inconnu'}</span>
                    <span style={G.tag(STATUS_COLOR[lead.status] || C.muted, STATUS_BG[lead.status])}>{lead.status}</span>
                    {(lead.tags || []).map(t => <TagBadge key={t} tag={t} />)}
                  </div>
                  <div style={{ fontSize: 12, color: C.textSub }}>
                    {lead.source} · {lead.country} {lead.city ? `· ${lead.city}` : ''} · {_fmtDateOnly(lead.detected_at)}
                  </div>
                  {lead.gemini_analysis?.resume && (
                    <div style={{ fontSize: 12, color: C.textMut, marginTop: 4 }}>{lead.gemini_analysis.resume.slice(0,100)}…</div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: scoreColor(lead.score) }}>{lead.score}</div>
                  <div style={{ fontSize: 10, color: C.muted }}>score</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {query && results.length === 0 && !loading && (
          <div style={{ padding: '24px 20px', textAlign: 'center', color: C.textSub, fontSize: 14 }}>
            Aucun lead trouvé pour «&nbsp;{query}&nbsp;»
          </div>
        )}

        {!query && (
          <div style={{ padding: '16px 20px', fontSize: 12, color: C.muted }}>
            Cherche dans : entreprises, contacts, villes, tags, résumés IA, notes…
          </div>
        )}
      </div>
    </div>
  );
}

// ── Components ────────────────────────────────────────────────────────────────
function Login({ onLogin }) {
  const [pwd, setPwd] = useState(''); const [err, setErr] = useState(''); const [loading, setLoading] = useState(false);
  const submit = async () => {
    setLoading(true); setErr('');
    try { const { token } = await api('/login', { method: 'POST', body: { password: pwd } }); localStorage.setItem('ld_token', token); onLogin(); }
    catch (e) { setErr(String(e)); }
    setLoading(false);
  };
  return (
    <div style={{ minHeight: '100vh', background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Inter, sans-serif' }}>
      <div style={{ ...G.card, width: 360, textAlign: 'center' }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🎯</div>
        <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontSize: 22, fontWeight: 700, color: C.text, marginBottom: 4 }}>Lead Detector</div>
        <div style={{ color: C.textSub, fontSize: 13, marginBottom: 28 }}>ACEP · Smart Mirror AI</div>
        <input type="password" placeholder="Mot de passe" value={pwd} onChange={e => setPwd(e.target.value)} onKeyDown={e => e.key === 'Enter' && submit()} style={{ ...G.input, marginBottom: 12, textAlign: 'center' }} autoFocus />
        {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 10 }}>{err}</div>}
        <button onClick={submit} disabled={loading} style={{ ...G.btn, width: '100%', opacity: loading ? .6 : 1 }}>{loading ? 'Connexion…' : 'Accéder'}</button>
      </div>
    </div>
  );
}

function ScoreBar({ score }) {
  const color = score >= 80 ? C.green : score >= 60 ? C.accentHi : score >= 40 ? C.amber : C.muted;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, background: C.border, borderRadius: 3, height: 6 }}>
        <div style={{ width: `${score}%`, height: 6, borderRadius: 3, background: color, transition: 'width .4s' }} />
      </div>
      <span style={{ color: C.text, fontSize: 13, fontWeight: 600, minWidth: 36 }}>{score}</span>
    </div>
  );
}

const SIGNAL_LABELS = {
  innovation: '⚡ Innovation', experience_client: '✨ Expérience client', verres_premium: '💎 Verres premium',
  ouverture: '🏪 Ouverture/rénov.', ia_digital: '🤖 IA / Digital', salon: '🎪 Salon',
  chaine: '🔗 Chaîne/Réseau', recrutement: '👥 Recrutement', telemedecine: '🏥 Télémédecine', mesure: '📐 Mesure optique',
};

function SignalPills({ signals }) {
  const active = Object.entries(signals || {}).filter(([, v]) => v);
  if (!active.length) return <span style={{ color: C.muted, fontSize: 12 }}>Aucun signal</span>;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {active.map(([k]) => <span key={k} style={{ ...G.tag(C.accent), fontSize: 11, padding: '2px 8px' }}>{SIGNAL_LABELS[k] || k}</span>)}
    </div>
  );
}

// ── Action History ────────────────────────────────────────────────────────────
function ActionHistory({ leadId }) {
  const [actions, setActions] = useState([]);
  const [type, setType] = useState('note');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api(`/leads/${leadId}/actions`).then(setActions).catch(() => {});
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!note.trim() && type === 'note') return;
    setSaving(true);
    await api(`/leads/${leadId}/actions`, { method: 'POST', body: { type, note } });
    setNote(''); load();
    setSaving(false);
  };

  const del = async (actionId) => {
    await api(`/leads/${leadId}/actions/${actionId}`, { method: 'DELETE' });
    load();
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 12 }}>📋 Historique des actions</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <select value={type} onChange={e => setType(e.target.value)} style={{ ...G.select, flex: '0 0 auto' }}>
          {ACTION_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <input value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === 'Enter' && add()} placeholder="Note ou commentaire…" style={{ ...G.input, flex: 1 }} />
        <button onClick={add} disabled={saving} style={{ ...G.btn, flex: '0 0 auto', opacity: saving ? .6 : 1 }}>+ Ajouter</button>
      </div>
      {actions.length === 0 ? (
        <div style={{ color: C.muted, fontSize: 13, textAlign: 'center', padding: '12px 0' }}>Aucune action enregistrée</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {actions.map(a => (
            <div key={a.id} style={{ background: C.bg, borderRadius: 8, padding: '10px 14px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 18 }}>{ACTION_ICONS[a.type] || '📝'}</span>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.accent, textTransform: 'capitalize' }}>{a.type}</span>
                  <span style={{ fontSize: 11, color: C.muted }}>{_fmtDate(a.date)}</span>
                </div>
                {a.note && <div style={{ fontSize: 13, color: C.text }}>{a.note}</div>}
              </div>
              <button onClick={() => del(a.id)} style={{ ...G.btnGhost, padding: '2px 8px', fontSize: 11, color: C.red, borderColor: C.red+'33' }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Contact Section ───────────────────────────────────────────────────────────
function ContactSection({ lead, onUpdate }) {
  const [form, setForm] = useState({
    contact_name: lead.contact_name || '',
    contact_role: lead.contact_role || '',
    contact_email: lead.contact_email || '',
    contact_linkedin: lead.contact_linkedin || '',
    contact_phone: lead.contact_phone || '',
  });
  const [saving, setSaving] = useState(false);
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [generatedEmail, setGeneratedEmail] = useState('');
  const [copied, setCopied] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    await onUpdate(lead.id, form);
    setSaving(false);
  };

  const generateEmail = async () => {
    setGeneratingEmail(true); setGeneratedEmail('');
    try {
      const r = await api(`/leads/${lead.id}/generate-email`, { method: 'POST' });
      setGeneratedEmail(r.email);
    } catch (e) { setGeneratedEmail('Erreur: ' + String(e)); }
    setGeneratingEmail(false);
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 12 }}>👤 Contact décideur</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 }}>
        <div><label style={G.label}>Nom</label><input value={form.contact_name} onChange={e => set('contact_name', e.target.value)} placeholder="Prénom Nom" style={G.input} /></div>
        <div><label style={G.label}>Rôle / Fonction</label><input value={form.contact_role} onChange={e => set('contact_role', e.target.value)} placeholder="Directeur réseau…" style={G.input} /></div>
        <div><label style={G.label}>Email</label><input value={form.contact_email} onChange={e => set('contact_email', e.target.value)} placeholder="contact@enseigne.fr" style={G.input} /></div>
        <div><label style={G.label}>Téléphone</label><input value={form.contact_phone} onChange={e => set('contact_phone', e.target.value)} placeholder="+33 6…" style={G.input} /></div>
        <div style={{ gridColumn: '1/-1' }}><label style={G.label}>Profil LinkedIn</label><input value={form.contact_linkedin} onChange={e => set('contact_linkedin', e.target.value)} placeholder="https://linkedin.com/in/…" style={G.input} /></div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={save} disabled={saving} style={{ ...G.btn, opacity: saving ? .6 : 1 }}>{saving ? 'Sauvegarde…' : '💾 Sauvegarder'}</button>
        <button onClick={generateEmail} disabled={generatingEmail} style={{ ...G.btn, background: C.purple, opacity: generatingEmail ? .6 : 1 }}>
          {generatingEmail ? '✉️ Génération…' : '✉️ Générer un email'}
        </button>
        {form.contact_email && <a href={`mailto:${form.contact_email}`} style={{ ...G.btnGhost, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>📧 Ouvrir</a>}
        {form.contact_linkedin && <a href={form.contact_linkedin} target="_blank" rel="noopener noreferrer" style={{ ...G.btnGhost, textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>💼 LinkedIn</a>}
      </div>
      {generatedEmail && (
        <div style={{ background: C.purpleBg, border: `1px solid ${C.purple}44`, borderRadius: 8, padding: 14 }}>
          <div style={{ fontSize: 12, color: C.purple, fontWeight: 600, marginBottom: 8 }}>✉️ Email généré</div>
          <pre style={{ color: C.text, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap', margin: 0, fontFamily: 'inherit' }}>{generatedEmail}</pre>
          <button onClick={() => copy(generatedEmail)} style={{ ...G.btnGhost, marginTop: 10, fontSize: 12, borderColor: C.purple+'44', color: copied ? C.green : C.textSub }}>
            {copied ? '✅ Copié !' : '📋 Copier'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Lead Card ─────────────────────────────────────────────────────────────────
function LeadCard({ lead, onUpdate, onDelete, onAnalyze }) {
  const [expanded, setExpanded] = useState(false);
  const [editStatus, setEditStatus] = useState(lead.status);
  const [editPriority, setEditPriority] = useState(lead.priority);
  const [saving, setSaving] = useState(false);
  const ga = lead.gemini_analysis || {};

  const save = async () => {
    setSaving(true);
    await onUpdate(lead.id, { status: editStatus, priority: editPriority });
    setSaving(false);
  };

  const isStale = lead.days_inactive > 7 && !['Contacté','Répondu','Non pertinent'].includes(lead.status);

  return (
    <div style={{ ...G.card, marginBottom: 12, borderColor: isStale ? C.amber+'44' : C.border }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 6 }}>
            <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 16, color: C.text }}>{lead.company || 'Inconnu'}</span>
            <span style={G.tag(PRIORITY_COLOR[lead.priority] || C.muted)}>{lead.priority}</span>
            <span style={G.tag(STATUS_COLOR[lead.status] || C.muted)}>{lead.status}</span>
            {lead.action_count > 0 && <span style={{ ...G.tag(C.purple, C.purpleBg), fontSize: 11 }}>{lead.action_count} action{lead.action_count > 1 ? 's' : ''}</span>}
            {isStale && <span style={{ ...G.tag(C.amber, C.amberBg), fontSize: 11 }}>⏰ {lead.days_inactive}j sans activité</span>}
            {(lead.tags || []).map(t => <TagBadge key={t} tag={t} />)}
          </div>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12, color: C.textSub }}>
            {lead.city && <span>📍 {lead.city}{lead.country !== 'France' ? `, ${lead.country}` : ''}</span>}
            <span>📌 {lead.company_type}</span>
            <span>🔍 {lead.source}</span>
            <span>🗓 {_fmtDateOnly(lead.detected_at)}</span>
            {lead.contact_name && <span>👤 {lead.contact_name}</span>}
          </div>
        </div>
        <div style={{ minWidth: 120 }}>
          <ScoreBar score={lead.score || 0} />
          <div style={{ fontSize: 11, color: C.textSub, marginTop: 4, textAlign: 'right' }}>Score /100</div>
        </div>
      </div>

      <div style={{ marginBottom: 12 }}><SignalPills signals={lead.detected_signals} /></div>

      <div style={{ fontSize: 13, color: C.textSub, lineHeight: 1.5, marginBottom: 12, borderLeft: `3px solid ${C.border}`, paddingLeft: 12 }}>
        {lead.publication_text?.slice(0,200)}{lead.publication_text?.length > 200 ? '…' : ''}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <button onClick={() => setExpanded(!expanded)} style={G.btnGhost}>{expanded ? '▲ Réduire' : '▼ Détails'}</button>
        {lead.publication_url && <a href={lead.publication_url} target="_blank" rel="noopener noreferrer" style={{ ...G.btnGhost, textDecoration: 'none', display: 'inline-block' }}>🔗 Source</a>}
        {!ga.resume && <button onClick={() => onAnalyze(lead)} style={{ ...G.btn, background: C.purple }}>🤖 Analyser avec IA</button>}
        <button onClick={() => onDelete(lead.id)} style={{ ...G.btnGhost, color: C.red, borderColor: C.red+'44', marginLeft: 'auto' }}>🗑</button>
      </div>

      {expanded && (
        <div style={{ marginTop: 20, borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
          {ga.resume && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.accent, marginBottom: 12 }}>🤖 Analyse IA</div>
              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ background: C.bg, borderRadius: 8, padding: '12px 16px' }}>
                  <div style={G.label}>Résumé</div><div style={{ color: C.text, fontSize: 13, lineHeight: 1.6 }}>{ga.resume}</div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ background: C.bg, borderRadius: 8, padding: '12px 16px' }}>
                    <div style={G.label}>Signal</div><div style={{ color: C.text, fontSize: 13 }}>{ga.signal_detecte}</div>
                  </div>
                  <div style={{ background: C.bg, borderRadius: 8, padding: '12px 16px' }}>
                    <div style={G.label}>Action recommandée</div><div style={{ color: C.text, fontSize: 13 }}>{ga.action_recommandee}</div>
                  </div>
                </div>
                <div style={{ background: C.bg, borderRadius: 8, padding: '12px 16px' }}>
                  <div style={G.label}>Intérêt pour ACEP</div><div style={{ color: C.text, fontSize: 13, lineHeight: 1.6 }}>{ga.interet_acep}</div>
                </div>
                {ga.message_contact && (
                  <div style={{ background: C.greenBg, border: `1px solid ${C.green}33`, borderRadius: 8, padding: '14px 16px' }}>
                    <div style={{ ...G.label, color: C.green }}>💬 Message de contact proposé</div>
                    <div style={{ color: C.text, fontSize: 13, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{ga.message_contact}</div>
                    <button onClick={() => navigator.clipboard.writeText(ga.message_contact)} style={{ ...G.btnGhost, marginTop: 10, fontSize: 12 }}>📋 Copier</button>
                  </div>
                )}
              </div>
            </div>
          )}

          <ContactSection lead={lead} onUpdate={onUpdate} />

          <div style={{ marginTop: 20, borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
              <div><label style={G.label}>Statut</label>
                <select value={editStatus} onChange={e => setEditStatus(e.target.value)} style={{ ...G.select, width: '100%' }}>
                  {STATUSES.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div><label style={G.label}>Priorité</label>
                <select value={editPriority} onChange={e => setEditPriority(e.target.value)} style={{ ...G.select, width: '100%' }}>
                  {PRIORITIES.map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <button onClick={save} disabled={saving} style={{ ...G.btn, opacity: saving ? .6 : 1 }}>{saving ? 'Sauvegarde…' : '💾 Sauvegarder'}</button>
          </div>

          <div style={{ marginTop: 20, borderTop: `1px solid ${C.border}`, paddingTop: 20 }}>
            <ActionHistory leadId={lead.id} />
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add Lead Modal ────────────────────────────────────────────────────────────
function AddLeadModal({ onClose, onSaved, hasGemini }) {
  const [form, setForm] = useState({ company: '', country: 'France', city: '', company_type: 'Indépendant', publication_url: '', source: 'Site web', publication_text: '', contact_available: '', status: 'À vérifier', notes: '', tags: [] });
  const [loading, setLoading] = useState(false); const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState(null); const [err, setErr] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const analyzeFirst = async () => {
    if (!form.publication_text.trim()) { setErr("Collez d'abord le texte de la publication"); return; }
    setAnalyzing(true); setErr('');
    try { const r = await api('/analyze', { method: 'POST', body: { publication_text: form.publication_text, company: form.company, source_url: form.publication_url } }); setPreview(r); }
    catch (e) { setErr(String(e)); }
    setAnalyzing(false);
  };

  const submit = async () => {
    if (!form.publication_text.trim()) { setErr('Le texte de publication est requis'); return; }
    setLoading(true); setErr('');
    try {
      const body = { ...form };
      if (preview) { body.score = preview.gemini?.score || preview.local_score; body.priority = preview.gemini?.priorite || 'Moyen'; body.detected_signals = preview.local_signals; body.gemini_analysis = preview.gemini || {}; }
      await api('/leads', { method: 'POST', body }); onSaved();
    } catch (e) { setErr(String(e)); }
    setLoading(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000a', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ ...G.card, width: '100%', maxWidth: 680, maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 18, color: C.text }}>➕ Ajouter un lead</span>
          <button onClick={onClose} style={{ ...G.btnGhost, padding: '6px 12px' }}>✕</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div><label style={G.label}>Entreprise *</label><input value={form.company} onChange={e => set('company', e.target.value)} placeholder="Nom de l'enseigne" style={G.input} /></div>
          <div><label style={G.label}>Type</label><select value={form.company_type} onChange={e => set('company_type', e.target.value)} style={{ ...G.select, width: '100%' }}>{TYPES.map(t => <option key={t}>{t}</option>)}</select></div>
          <div><label style={G.label}>Ville</label><input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Paris" style={G.input} /></div>
          <div><label style={G.label}>Pays</label><input value={form.country} onChange={e => set('country', e.target.value)} placeholder="France" style={G.input} /></div>
          <div><label style={G.label}>Source</label><select value={form.source} onChange={e => set('source', e.target.value)} style={{ ...G.select, width: '100%' }}>{SOURCES.map(s => <option key={s}>{s}</option>)}</select></div>
          <div><label style={G.label}>Contact disponible</label><input value={form.contact_available} onChange={e => set('contact_available', e.target.value)} placeholder="Nom / email" style={G.input} /></div>
        </div>
        <div style={{ marginBottom: 14 }}><label style={G.label}>URL de la publication</label><input value={form.publication_url} onChange={e => set('publication_url', e.target.value)} placeholder="https://…" style={G.input} /></div>
        <div style={{ marginBottom: 14 }}><label style={G.label}>Texte de la publication *</label><textarea value={form.publication_text} onChange={e => set('publication_text', e.target.value)} placeholder="Collez ici le contenu…" rows={5} style={{ ...G.input, resize: 'vertical' }} /></div>
        <div style={{ marginBottom: 14 }}><label style={G.label}>Notes internes</label><input value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Observations…" style={G.input} /></div>
        {preview && (
          <div style={{ background: C.greenBg, border: `1px solid ${C.green}33`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 600, color: C.green, marginBottom: 10 }}>✅ Analyse IA</div>
            <div style={{ fontSize: 13, color: C.text, marginBottom: 6 }}><b>Résumé :</b> {preview.gemini?.resume}</div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={G.tag(C.green)}>Score : {preview.gemini?.score || preview.local_score}/100</span>
              <span style={G.tag(PRIORITY_COLOR[preview.gemini?.priorite] || C.amber)}>{preview.gemini?.priorite}</span>
            </div>
          </div>
        )}
        {err && <div style={{ color: C.red, fontSize: 13, marginBottom: 12 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          {hasGemini && <button onClick={analyzeFirst} disabled={analyzing} style={{ ...G.btn, background: C.purple, opacity: analyzing ? .6 : 1 }}>{analyzing ? '🤖 Analyse…' : '🤖 Analyser d\'abord'}</button>}
          <button onClick={submit} disabled={loading} style={{ ...G.btn, opacity: loading ? .6 : 1 }}>{loading ? 'Création…' : '💾 Enregistrer'}</button>
          <button onClick={onClose} style={G.btnGhost}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

// ── Batch Analyze Banner ──────────────────────────────────────────────────────
function BatchBanner({ onDone }) {
  const [batchId, setBatchId] = useState(null);
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  const start = async () => {
    setStarting(true);
    try {
      const r = await api('/leads/analyze-batch', { method: 'POST' });
      setBatchId(r.batch_id);
      setStatus({ total: r.total, done: 0, errors: 0, status: 'running' });
      pollRef.current = setInterval(async () => {
        const s = await api(`/leads/analyze-batch/${r.batch_id}`);
        setStatus(s);
        if (s.status === 'done') { clearInterval(pollRef.current); onDone(); }
      }, 2000);
    } catch (e) { alert(String(e)); }
    setStarting(false);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  if (!status) {
    return (
      <button onClick={start} disabled={starting} style={{ ...G.btn, background: C.purple, opacity: starting ? .6 : 1 }}>
        {starting ? '⏳ Démarrage…' : '🤖 Analyser tous avec IA'}
      </button>
    );
  }

  const pct = status.total ? Math.round((status.done / status.total) * 100) : 0;
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 16px', display: 'flex', alignItems: 'center', gap: 14, flex: 1 }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: C.text, marginBottom: 6 }}>
          {status.status === 'done' ? `✅ Analyse terminée — ${status.done} leads traités` : `🤖 Analyse en cours… ${status.done}/${status.total}`}
          {status.errors > 0 && <span style={{ color: C.amber, marginLeft: 8 }}>({status.errors} erreurs)</span>}
        </div>
        <div style={{ background: C.border, borderRadius: 3, height: 6 }}>
          <div style={{ width: `${pct}%`, height: 6, borderRadius: 3, background: status.status === 'done' ? C.green : C.purple, transition: 'width .3s' }} />
        </div>
      </div>
      <span style={{ fontSize: 13, fontWeight: 600, color: C.textSub }}>{pct}%</span>
    </div>
  );
}

// ── Onglet Leads ──────────────────────────────────────────────────────────────
function TabLeads({ hasGemini, highlightId, onClearHighlight }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubSource, setFilterSubSource] = useState('');
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [sort, setSort] = useState('score');
  const [order, setOrder] = useState('desc');
  const [analyzingId, setAnalyzingId] = useState(null);
  const [toast, setToast] = useState('');
  const [showBatch, setShowBatch] = useState(false);
  const [exporting, setExporting] = useState(false);

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/leads?sort=${sort}&order=${order}`;
      if (filterStatus) url += `&status=${encodeURIComponent(filterStatus)}`;
      if (filterPriority) url += `&priority=${encodeURIComponent(filterPriority)}`;
      const data = await api(url);
      setLeads(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterStatus, filterPriority, sort, order]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => {
    if (highlightId && !loading) {
      setTimeout(() => {
        const el = document.getElementById(`lead-${highlightId}`);
        if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.style.outline = `2px solid ${C.accent}`; setTimeout(() => { el.style.outline = ''; onClearHighlight?.(); }, 2500); }
      }, 300);
    }
  }, [highlightId, loading]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const handleUpdate = async (id, updates) => {
    await api(`/leads/${id}`, { method: 'PUT', body: updates });
    loadLeads();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce lead ?')) return;
    await api(`/leads/${id}`, { method: 'DELETE' });
    loadLeads(); showToast('Lead supprimé');
  };

  const handleAnalyze = async (lead) => {
    if (!hasGemini) { showToast('Configure la clé Gemini dans les paramètres'); return; }
    setAnalyzingId(lead.id);
    try {
      const r = await api('/analyze', { method: 'POST', body: { publication_text: lead.publication_text, company: lead.company, source_url: lead.publication_url } });
      await api(`/leads/${lead.id}`, { method: 'PUT', body: { score: r.gemini?.score || r.local_score, priority: r.gemini?.priorite || lead.priority, gemini_analysis: r.gemini || {} } });
      loadLeads(); showToast('✅ Analyse IA terminée');
    } catch (e) { showToast('❌ ' + String(e)); }
    setAnalyzingId(null);
  };

  const doExport = async () => {
    setExporting(true);
    const token = localStorage.getItem('ld_token');
    let url = `${API}/leads/export/csv?1=1`;
    if (filterStatus) url += `&status=${encodeURIComponent(filterStatus)}`;
    if (filterPriority) url += `&priority=${encodeURIComponent(filterPriority)}`;
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `leads_acep_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    setExporting(false);
  };

  // Tri cliquable
  const SortBtn = ({ field, label }) => {
    const active = sort === field;
    return (
      <button onClick={() => { if (active) setOrder(o => o === 'desc' ? 'asc' : 'desc'); else { setSort(field); setOrder('desc'); } }}
        style={{ ...G.btnGhost, fontSize: 12, padding: '5px 12px', borderColor: active ? C.accent : C.border, color: active ? C.accent : C.textSub }}>
        {label} {active ? (order === 'desc' ? '↓' : '↑') : ''}
      </button>
    );
  };

  const availableCountries = [...new Set(leads.map(l => _normalizeCountry(l.country)))].sort();
  const availableTypes = [...new Set(leads.map(l => _normalizeType(l.company_type)))].sort();

  const availableTags = [...new Set(leads.flatMap(l => l.tags || []))].sort();
  const afterCountry = leads.filter(l => !filterCountry || _normalizeCountry(l.country) === filterCountry);
  const afterType = afterCountry.filter(l => !filterType || _normalizeType(l.company_type) === filterType);
  const afterCat = afterType.filter(l => { if (!filterCategory) return true; const cat = SOURCE_CATEGORIES[filterCategory]; return cat ? cat.match(l.source) : true; });

  const subSourceCounts = {};
  afterCat.forEach(l => { const lbl = _sourceLabel(l); subSourceCounts[lbl] = (subSourceCounts[lbl] || 0) + 1; });

  const filtered = afterCat.filter(l => {
    if (filterSubSource && _sourceLabel(l) !== filterSubSource) return false;
    if (filterTag && !(l.tags || []).includes(filterTag)) return false;
    if (search && !l.company?.toLowerCase().includes(search.toLowerCase()) && !l.publication_text?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const catCounts = {};
  afterType.forEach(l => { Object.entries(SOURCE_CATEGORIES).forEach(([cat, { match }]) => { if (match(l.source)) catCounts[cat] = (catCounts[cat] || 0) + 1; }); });

  const pill = (active) => ({ ...G.btnGhost, fontSize: 13, padding: '6px 14px', borderColor: active ? C.accent : C.border, color: active ? C.accent : C.textSub, background: active ? C.accent+'18' : 'transparent' });

  const pendingCount = leads.filter(l => !l.gemini_analysis?.resume).length;

  return (
    <div>
      {/* Ligne 1 : recherche + selects + actions */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Rechercher…" style={{ ...G.input, width: 180 }} />
        <select value={filterCountry} onChange={e => { setFilterCountry(e.target.value); setFilterCategory(''); setFilterSubSource(''); }} style={G.select}>
          <option value="">Tous pays</option>
          {availableCountries.map(c => <option key={c}>{c}</option>)}
        </select>
        <select value={filterType} onChange={e => { setFilterType(e.target.value); setFilterCategory(''); setFilterSubSource(''); }} style={G.select}>
          <option value="">Tous types</option>
          {availableTypes.map(t => <option key={t}>{t}</option>)}
        </select>
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={G.select}>
          <option value="">Toutes priorités</option>
          {PRIORITIES.map(p => <option key={p}>{p}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={G.select}>
          <option value="">Tous statuts</option>
          {STATUSES.map(s => <option key={s}>{s}</option>)}
        </select>
        {availableTags.length > 0 && (
          <select value={filterTag} onChange={e => setFilterTag(e.target.value)} style={G.select}>
            <option value="">Tous tags</option>
            {availableTags.map(t => <option key={t}>{t}</option>)}
          </select>
        )}
        <span style={{ color: C.textSub, fontSize: 13 }}>{filtered.length} leads</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button onClick={doExport} disabled={exporting} style={{ ...G.btnGhost, fontSize: 13, opacity: exporting ? .6 : 1 }}>
            {exporting ? '…' : '📥 CSV'}
          </button>
          <button onClick={() => setShowAdd(true)} style={G.btn}>➕ Ajouter</button>
        </div>
      </div>

      {/* Ligne 2 : tri + analyse batch */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: C.muted }}>Trier :</span>
        <SortBtn field="score" label="Score" />
        <SortBtn field="date" label="Date" />
        <SortBtn field="country" label="Pays" />
        <SortBtn field="activity" label="Activité" />
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flex: 1, justifyContent: 'flex-end' }}>
          {hasGemini && pendingCount > 0 && !showBatch && (
            <button onClick={() => setShowBatch(true)} style={{ ...G.btn, background: C.purple, fontSize: 13 }}>
              🤖 Analyser {pendingCount} leads avec IA
            </button>
          )}
          {showBatch && <BatchBanner onDone={() => { loadLeads(); setShowBatch(false); showToast('✅ Analyse batch terminée'); }} />}
        </div>
      </div>

      {/* Ligne 3 : sources niveau 1 */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <button onClick={() => { setFilterCategory(''); setFilterSubSource(''); }} style={pill(!filterCategory)}>
          Toutes sources <span style={{ fontSize: 11, marginLeft: 4, opacity: .7 }}>{afterType.length}</span>
        </button>
        {Object.entries(SOURCE_CATEGORIES).map(([cat, { icon }]) => {
          const count = catCounts[cat] || 0; if (!count) return null;
          return <button key={cat} onClick={() => { if (filterCategory === cat) { setFilterCategory(''); setFilterSubSource(''); } else { setFilterCategory(cat); setFilterSubSource(''); } }} style={pill(filterCategory === cat)}>{icon} {cat} <span style={{ fontSize: 11, marginLeft: 4, opacity: .7 }}>{count}</span></button>;
        })}
      </div>

      {/* Ligne 4 : sous-sources niveau 2 */}
      {filterCategory && Object.keys(subSourceCounts).length > 0 && (
        <div style={{ background: '#ffffff', border: `1px solid ${C.border}`, borderRadius: 10, padding: '10px 14px', marginBottom: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em' }}>{SOURCE_CATEGORIES[filterCategory]?.icon} {filterCategory} →</span>
          <button onClick={() => setFilterSubSource('')} style={{ ...G.btnGhost, fontSize: 12, padding: '4px 12px', borderColor: !filterSubSource ? C.accent : C.border, color: !filterSubSource ? C.accent : C.textSub, background: !filterSubSource ? C.accentBg : 'transparent' }}>Tous <span style={{ opacity: .6 }}>{afterCat.length}</span></button>
          {Object.entries(subSourceCounts).sort(([,a],[,b]) => b-a).map(([lbl, count]) => {
            const active = filterSubSource === lbl;
            return <button key={lbl} onClick={() => setFilterSubSource(active ? '' : lbl)} style={{ ...G.btnGhost, fontSize: 12, padding: '4px 12px', borderColor: active ? C.accent : C.border, color: active ? C.accent : C.textSub, background: active ? C.accentBg : 'transparent' }}>{lbl} <span style={{ opacity: .6 }}>{count}</span></button>;
          })}
        </div>
      )}

      {/* Liste */}
      {loading ? (
        <div style={{ textAlign: 'center', color: C.textSub, padding: 40 }}>Chargement…</div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.textSub, padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎯</div>
          <div style={{ fontSize: 16, color: C.text, marginBottom: 8 }}>Aucun lead</div>
          <div style={{ fontSize: 13 }}>Lance un scan ou ajoute un lead manuellement</div>
        </div>
      ) : (
        filtered.map(lead => (
          <div key={lead.id} id={`lead-${lead.id}`} style={{ opacity: analyzingId === lead.id ? .5 : 1, transition: 'opacity .3s, outline .3s' }}>
            <LeadCard lead={lead} onUpdate={handleUpdate} onDelete={handleDelete} onAnalyze={handleAnalyze} />
          </div>
        ))
      )}

      {showAdd && <AddLeadModal hasGemini={hasGemini} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadLeads(); showToast('✅ Lead ajouté'); }} />}
      {toast && <div style={{ position: 'fixed', bottom: 24, right: 24, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 20px', color: C.text, fontSize: 14, zIndex: 300, boxShadow: C.shadowMd }}>{toast}</div>}
    </div>
  );
}

// ── Onglet Recherche ──────────────────────────────────────────────────────────
// Clé localStorage pour persister les résultats de recherche
const SEARCH_STORAGE_KEY = 'ld_search_results';

function _loadStoredResults() {
  try { return JSON.parse(localStorage.getItem(SEARCH_STORAGE_KEY) || '[]'); } catch { return []; }
}
function _saveStoredResults(results) {
  try { localStorage.setItem(SEARCH_STORAGE_KEY, JSON.stringify(results)); } catch {}
}
// Fusionner de nouveaux résultats sans doublon (par URL)
function _mergeResults(existing, incoming) {
  const seen = new Set(existing.map(r => r.url));
  const added = incoming.filter(r => r.url && !seen.has(r.url));
  return [...existing, ...added];
}

function TabSearch({ hasSerper }) {
  const [queries, setQueries] = useState([]);
  const [customQuery, setCustomQuery] = useState('');
  // Résultats persistés : chargés depuis localStorage au montage
  const [results, setResults] = useState(() => _loadStoredResults());
  const [doneQueries, setDoneQueries] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem('ld_done_queries') || '[]')); } catch { return new Set(); }
  });
  const [scanning, setScanning] = useState(false);
  const [searching, setSearching] = useState(false);
  const [scans, setScans] = useState([]);
  const [scanPoll, setScanPoll] = useState(null);
  const [toast, setToast] = useState('');
  const [savedIds, setSavedIds] = useState(() => {
    // Pré-remplir avec les URLs déjà dans les leads
    return new Set();
  });
  const [filterQuery, setFilterQuery] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  // Charger les URLs déjà converties en leads
  useEffect(() => {
    api('/search/queries').then(setQueries).catch(() => {});
    api('/search/scans').then(setScans).catch(() => {});
    // Charger les URLs existantes dans les leads pour marquer comme "déjà ajouté"
    api('/leads').then(leads => {
      setSavedIds(new Set(leads.map(l => l.publication_url).filter(Boolean)));
    }).catch(() => {});
    return () => { if (scanPoll) clearInterval(scanPoll); };
  }, []);

  // Persister résultats à chaque changement
  useEffect(() => { _saveStoredResults(results); }, [results]);
  useEffect(() => {
    try { localStorage.setItem('ld_done_queries', JSON.stringify([...doneQueries])); } catch {}
  }, [doneQueries]);

  const doSearch = async (q) => {
    if (!hasSerper) { showToast('Configure la clé Serper dans les paramètres'); return; }
    if (doneQueries.has(q)) {
      showToast(`ℹ️ "${q}" déjà effectuée — résultats déjà inclus`);
      return;
    }
    setSearching(true);
    try {
      const r = await api('/search', { method: 'POST', body: { query: q, num: 10 } });
      const incoming = r.results || [];
      setResults(prev => {
        const merged = _mergeResults(prev, incoming.map(x => ({ ...x, _query: q })));
        return merged;
      });
      setDoneQueries(prev => new Set([...prev, q]));
      showToast(`✅ ${incoming.length} résultats · ${incoming.filter(x => !results.find(e => e.url === x.url)).length} nouveaux`);
    } catch (e) { showToast('❌ ' + String(e)); }
    setSearching(false);
  };

  const startBatch = async () => {
    if (!hasSerper) { showToast('Configure la clé Serper'); return; }
    setScanning(true);
    try {
      const r = await api('/search/batch', { method: 'POST' });
      showToast(`🚀 ${r.message}`);
      const poll = setInterval(async () => {
        const s = await api('/search/scans'); setScans(s);
        const cur = s.find(x => x.id === r.scan_id);
        if (cur?.status === 'done') {
          clearInterval(poll);
          setScanning(false);
          // Recharger les leads pour mettre à jour savedIds
          api('/leads').then(leads => setSavedIds(new Set(leads.map(l => l.publication_url).filter(Boolean))));
          showToast(`✅ Scan terminé — ${cur.new_leads} nouveaux leads créés automatiquement`);
          api('/search/scans').then(setScans);
        }
      }, 2500);
      setScanPoll(poll);
    } catch (e) { showToast('❌ ' + String(e)); setScanning(false); }
  };

  const saveResult = async (r) => {
    const text = `${r.title}. ${r.snippet}`;
    await api('/leads', { method: 'POST', body: {
      company: r.title?.split(' - ')[0]?.slice(0, 80) || 'Inconnu',
      country: 'France', city: '', company_type: 'Indépendant',
      publication_url: r.url,
      source: r.url?.includes('linkedin') ? 'LinkedIn' : 'Site web',
      publication_text: text, status: 'À vérifier', notes: r._query ? `Via: «${r._query}»` : '',
    }});
    setSavedIds(s => new Set([...s, r.url]));
    showToast('✅ Lead ajouté');
  };

  const clearResults = () => {
    if (!window.confirm("Effacer tous les résultats et l'historique des recherches ?")) return;
    setResults([]);
    setDoneQueries(new Set());
    localStorage.removeItem(SEARCH_STORAGE_KEY);
    localStorage.removeItem('ld_done_queries');
    showToast('🗑 Résultats effacés');
  };

  const lastScan = scans[0];

  // Filtrer les résultats affichés
  const visibleResults = results.filter(r =>
    !filterQuery || r._query === filterQuery ||
    r.title?.toLowerCase().includes(filterQuery.toLowerCase())
  );

  // Requêtes déjà effectuées vs pas encore
  const pendingQueries = queries.filter(({ query }) => !doneQueries.has(query));
  const doneQueriesList = queries.filter(({ query }) => doneQueries.has(query));

  return (
    <div>
      {/* Scan auto */}
      <div style={{ ...G.card, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 16, color: C.text, marginBottom: 6 }}>🔄 Scan automatique</div>
            <div style={{ color: C.textSub, fontSize: 13 }}>
              Lance {queries.length} requêtes prédéfinies · {doneQueriesList.length} déjà effectuées · {pendingQueries.length} restantes
            </div>
            {lastScan && (
              <div style={{ marginTop: 10, fontSize: 12, color: C.textSub }}>
                Dernier : {_fmtDate(lastScan.started_at)} — {lastScan.trigger === 'auto' ? '🔁 auto' : '▶️ manuel'} —
                {lastScan.status === 'done' ? ` ${lastScan.new_leads} leads` : ` En cours (${lastScan.done_queries}/${lastScan.total_queries})`}
              </div>
            )}
          </div>
          <button onClick={startBatch} disabled={scanning || !hasSerper} style={{ ...G.btn, background: scanning ? C.muted : C.green, whiteSpace: 'nowrap' }}>
            {scanning ? '⏳ Scan en cours…' : '🚀 Lancer le scan'}
          </button>
        </div>
      </div>

      {/* Recherche manuelle */}
      <div style={{ ...G.card, marginBottom: 20 }}>
        <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, fontSize: 15, color: C.text, marginBottom: 16 }}>🔍 Recherche manuelle</div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
          <input value={customQuery} onChange={e => setCustomQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && customQuery.trim() && doSearch(customQuery)} placeholder="opticien innovation…" style={{ ...G.input, flex: 1 }} />
          <button onClick={() => doSearch(customQuery)} disabled={searching || !customQuery.trim()} style={G.btn}>{searching ? '…' : 'Chercher'}</button>
        </div>

        {/* Requêtes pas encore faites */}
        {pendingQueries.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: C.textSub, marginBottom: 8 }}>À effectuer ({pendingQueries.length}) :</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {pendingQueries.map(({ query, lang }) => (
                <button key={query} onClick={() => { setCustomQuery(query); doSearch(query); }}
                  style={{ ...G.btnGhost, fontSize: 12, padding: '5px 12px' }}>
                  {lang === 'en' ? '🇬🇧' : '🇫🇷'} {query}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Requêtes déjà faites */}
        {doneQueriesList.length > 0 && (
          <div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Déjà effectuées ({doneQueriesList.length}) :</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {doneQueriesList.map(({ query, lang }) => (
                <button key={query} onClick={() => setFilterQuery(filterQuery === query ? '' : query)}
                  style={{ ...G.btnGhost, fontSize: 12, padding: '5px 12px', opacity: .6,
                    borderColor: filterQuery === query ? C.accent : C.border,
                    color: filterQuery === query ? C.accent : C.muted,
                    background: filterQuery === query ? C.accentBg : '#fff' }}>
                  ✓ {lang === 'en' ? '🇬🇧' : '🇫🇷'} {query}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Résultats */}
      {results.length > 0 && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 600, color: C.text }}>
              {visibleResults.length} résultat{visibleResults.length !== 1 ? 's' : ''}
              {filterQuery && <span style={{ fontSize: 13, fontWeight: 400, color: C.textSub }}> · filtrés sur «{filterQuery}»</span>}
              {filterQuery && <button onClick={() => setFilterQuery('')} style={{ ...G.btnGhost, fontSize: 11, padding: '2px 8px', marginLeft: 8 }}>✕</button>}
              <span style={{ fontSize: 12, color: C.muted, fontWeight: 400, marginLeft: 12 }}>
                {savedIds.size > 0 && `${results.filter(r => savedIds.has(r.url)).length} déjà en leads`}
              </span>
            </div>
            <button onClick={clearResults} style={{ ...G.btnGhost, fontSize: 12, padding: '5px 12px', color: C.red, borderColor: C.red+'33' }}>
              🗑 Tout effacer
            </button>
          </div>

          {visibleResults.map((r, i) => {
            const isSaved = savedIds.has(r.url);
            return (
              <div key={r.url || i} style={{ ...G.card, marginBottom: 10, opacity: isSaved ? .7 : 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: C.text, marginBottom: 4, fontSize: 14 }}>{r.title}</div>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: C.muted }}>{r.url?.slice(0,55)}…</span>
                      {r._query && (
                        <button onClick={() => setFilterQuery(filterQuery === r._query ? '' : r._query)}
                          style={{ ...G.tag(C.accent, C.accentBg), fontSize: 10, padding: '1px 7px', cursor: 'pointer', border: 'none' }}>
                          {r._query}
                        </button>
                      )}
                    </div>
                    <div style={{ fontSize: 13, color: C.textSub, lineHeight: 1.5 }}>{r.snippet}</div>
                    {r.date && <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>📅 {r.date}</div>}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 110, flexShrink: 0 }}>
                    <button onClick={() => saveResult(r)} disabled={isSaved}
                      style={{ ...G.btn, fontSize: 12, opacity: isSaved ? .4 : 1, background: isSaved ? C.green : C.accent }}>
                      {isSaved ? '✓ En lead' : '➕ Lead'}
                    </button>
                    <a href={r.url} target="_blank" rel="noopener noreferrer"
                      style={{ ...G.btnGhost, fontSize: 12, textDecoration: 'none', display: 'block', textAlign: 'center' }}>
                      🔗 Ouvrir
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {results.length === 0 && (
        <div style={{ textAlign: 'center', color: C.muted, padding: '48px 0' }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 15, color: C.textSub, marginBottom: 6 }}>Aucune recherche effectuée</div>
          <div style={{ fontSize: 13 }}>Lance le scan auto ou utilise une requête manuelle</div>
        </div>
      )}

      {toast && <div style={{ position: 'fixed', bottom: 24, right: 24, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 20px', color: C.text, fontSize: 14, zIndex: 300, boxShadow: C.shadowMd }}>{toast}</div>}
    </div>
  );
}

// ── Onglet Dashboard ──────────────────────────────────────────────────────────
function TabDashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api('/leads/stats').then(setStats).catch(()=>{}); }, []);
  if (!stats) return <div style={{ color: C.textSub, padding: 40, textAlign: 'center' }}>Chargement…</div>;

  const StatBox = ({ label, value, color = C.text, sub, warn }) => (
    <div style={{ ...G.card, textAlign: 'center', borderColor: warn ? C.amber+'55' : C.border }}>
      <div style={{ fontSize: 36, fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>{label}</div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );

  const BarChart = ({ title, data, colors }) => (
    <div style={G.card}>
      <div style={{ fontWeight: 600, color: C.text, marginBottom: 16, fontSize: 14 }}>{title}</div>
      {Object.entries(data).sort(([,a],[,b]) => b-a).map(([k, v]) => (
        <div key={k} style={{ marginBottom: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: C.textSub, marginBottom: 5 }}>
            <span style={{ color: colors?.[k] || C.text }}>{k}</span>
            <span style={{ fontWeight: 600, color: C.text }}>{v}</span>
          </div>
          <div style={{ background: C.border, borderRadius: 3, height: 6 }}>
            <div style={{ width: `${(v/stats.total)*100}%`, height: 6, borderRadius: 3, background: colors?.[k] || C.accent, transition: 'width .4s' }} />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16 }}>
        <StatBox label="Leads total" value={stats.total} color={C.accentHi} />
        <StatBox label="Score moyen" value={stats.avg_score} sub="/100" color={stats.avg_score >= 60 ? C.green : C.amber} />
        <StatBox label="Très fort" value={stats.by_priority?.['Très fort'] || 0} color={C.green} />
        <StatBox label="Stagnants" value={stats.stale_count || 0} color={C.amber} sub="> 7j sans activité" warn={stats.stale_count > 0} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <BarChart title="Par priorité" data={stats.by_priority || {}} colors={{'Très fort':C.green,'Fort':C.accentHi,'Moyen':C.amber,'Faible':C.muted}} />
        <BarChart title="Par statut" data={stats.by_status || {}} colors={STATUS_COLOR} />
      </div>
      <BarChart title="Par source" data={stats.by_source || {}} />
    </div>
  );
}

// ── Onglet Config ─────────────────────────────────────────────────────────────
function TabConfig({ onConfigChange }) {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ admin_password: '', gemini_api_key: '', serper_api_key: '', weekly_scan_enabled: false, weekly_scan_day: 0, weekly_scan_hour: 8 });
  const [saving, setSaving] = useState(false); const [msg, setMsg] = useState('');
  const DAYS = ['Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi','Dimanche'];

  useEffect(() => { api('/config').then(d => { setCfg(d); setForm(f => ({ ...f, weekly_scan_enabled: d.weekly_scan_enabled || false, weekly_scan_day: d.weekly_scan_day ?? 0, weekly_scan_hour: d.weekly_scan_hour ?? 8 })); }).catch(()=>{}); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const body = { weekly_scan_enabled: form.weekly_scan_enabled, weekly_scan_day: form.weekly_scan_day, weekly_scan_hour: form.weekly_scan_hour };
      if (form.admin_password) body.admin_password = form.admin_password;
      if (form.gemini_api_key) body.gemini_api_key = form.gemini_api_key;
      if (form.serper_api_key) body.serper_api_key = form.serper_api_key;
      await api('/config', { method: 'PUT', body });
      setMsg('✅ Configuration sauvegardée');
      const newCfg = await api('/config'); setCfg(newCfg); onConfigChange();
      setForm(f => ({ ...f, admin_password: '', gemini_api_key: '', serper_api_key: '' }));
    } catch (e) { setMsg('❌ ' + String(e)); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ ...G.card, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, color: C.text, fontSize: 15, marginBottom: 16 }}>🔑 Clés API</div>
        <div style={{ marginBottom: 16 }}>
          <label style={G.label}>Clé Gemini</label>
          <div style={{ fontSize: 12, color: cfg?.has_gemini ? C.green : C.amber, marginBottom: 8 }}>{cfg?.has_gemini ? '✅ Configurée' : '⚠️ Non configurée'}</div>
          <input type="password" value={form.gemini_api_key} onChange={e => set('gemini_api_key', e.target.value)} placeholder="AIzaSy… (laisser vide pour ne pas modifier)" style={G.input} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 5 }}>→ <a href="https://aistudio.google.com" target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>aistudio.google.com</a> (gratuit)</div>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label style={G.label}>Clé Serper</label>
          <div style={{ fontSize: 12, color: cfg?.has_serper ? C.green : C.amber, marginBottom: 8 }}>{cfg?.has_serper ? '✅ Configurée' : '⚠️ Non configurée'}</div>
          <input type="password" value={form.serper_api_key} onChange={e => set('serper_api_key', e.target.value)} placeholder="… (laisser vide pour ne pas modifier)" style={G.input} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 5 }}>→ <a href="https://serper.dev" target="_blank" rel="noopener noreferrer" style={{ color: C.accent }}>serper.dev</a> — 2 500 req/mois gratuites</div>
        </div>
      </div>

      <div style={{ ...G.card, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, color: C.text, fontSize: 15, marginBottom: 16 }}>🔁 Scan hebdomadaire automatique</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.weekly_scan_enabled} onChange={e => set('weekly_scan_enabled', e.target.checked)} style={{ width: 16, height: 16 }} />
            <span style={{ color: C.text, fontSize: 14 }}>Activer le scan automatique</span>
          </label>
        </div>
        {form.weekly_scan_enabled && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={G.label}>Jour</label>
              <select value={form.weekly_scan_day} onChange={e => set('weekly_scan_day', parseInt(e.target.value))} style={{ ...G.select, width: '100%' }}>
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </div>
            <div>
              <label style={G.label}>Heure</label>
              <select value={form.weekly_scan_hour} onChange={e => set('weekly_scan_hour', parseInt(e.target.value))} style={{ ...G.select, width: '100%' }}>
                {Array.from({length:24},(_,i)=><option key={i} value={i}>{String(i).padStart(2,'0')}:00</option>)}
              </select>
            </div>
          </div>
        )}
      </div>

      <div style={{ ...G.card, marginBottom: 16 }}>
        <div style={{ fontWeight: 600, color: C.text, fontSize: 15, marginBottom: 16 }}>🔐 Sécurité</div>
        <label style={G.label}>Nouveau mot de passe</label>
        <input type="password" value={form.admin_password} onChange={e => set('admin_password', e.target.value)} placeholder="Laisser vide pour ne pas changer" style={G.input} />
      </div>

      {msg && <div style={{ marginBottom: 12, fontSize: 13, color: msg.startsWith('✅') ? C.green : C.red }}>{msg}</div>}
      <button onClick={save} disabled={saving} style={{ ...G.btn, opacity: saving ? .6 : 1 }}>{saving ? 'Sauvegarde…' : '💾 Sauvegarder'}</button>
    </div>
  );
}

// ── Onglet Acuité ─────────────────────────────────────────────────────────────
function TabAcuite({ hasGemini }) {
  const [scans, setScans] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [toast, setToast] = useState('');
  const [analyzingId, setAnalyzingId] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());
  const pollRef = useRef(null);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const loadScans = useCallback(() => {
    api('/acuite/scans').then(setScans).catch(() => {});
  }, []);

  useEffect(() => { loadScans(); return () => { if (pollRef.current) clearInterval(pollRef.current); }; }, [loadScans]);

  const startScan = async () => {
    setScanning(true); setPreview(null);
    try {
      const r = await api('/acuite/scan', { method: 'POST' });
      showToast(`🚀 ${r.message}`);
      pollRef.current = setInterval(async () => {
        const s = await api('/acuite/scans');
        setScans(s);
        const cur = s.find(x => x.id === r.scan_id);
        if (cur?.status === 'done') {
          clearInterval(pollRef.current);
          setScanning(false);
          showToast(`✅ Scan terminé — ${cur.new_leads} nouveaux leads · ${cur.total_articles} articles analysés`);
        }
      }, 3000);
    } catch (e) { showToast('❌ ' + String(e)); setScanning(false); }
  };

  const doPreview = async () => {
    setPreviewing(true); setPreview(null);
    try {
      const r = await api('/acuite/preview');
      setPreview(r);
    } catch (e) { showToast('❌ ' + String(e)); }
    setPreviewing(false);
  };

  const saveArticleAsLead = async (art) => {
    const text = art.title + '. ' + (art.excerpt || '');
    await api('/leads', {
      method: 'POST',
      body: {
        company: art.company || art.title?.split(' ')[0] || 'Inconnu',
        country: 'France', city: '', company_type: 'Indépendant',
        publication_url: art.url, source: 'Acuité',
        publication_text: text,
        status: 'À vérifier',
        notes: `Acuité · ${art.section || 'Actualités'} · ${art.date || ''}`.trim(),
      }
    });
    setSavedIds(s => new Set([...s, art.url]));
    showToast('✅ Lead ajouté');
  };

  const analyzeAndSave = async (art) => {
    if (!hasGemini) { showToast('Configure la clé Gemini dans les paramètres'); return; }
    setAnalyzingId(art.url);
    const text = art.title + '. ' + (art.excerpt || '');
    try {
      const r = await api('/analyze', { method: 'POST', body: { publication_text: text, company: art.title?.split(' ')[0] || '', source_url: art.url } });
      await api('/leads', {
        method: 'POST',
        body: {
          company: r.gemini?.message_contact?.split('\n')[0]?.replace(/^Bonjour.*?,\s*/,'')?.slice(0,60) || art.title?.slice(0,60) || 'Inconnu',
          country: 'France', city: '', company_type: 'Indépendant',
          publication_url: art.url, source: 'Acuité',
          publication_text: text,
          score: r.gemini?.score || r.local_score,
          priority: r.gemini?.priorite || 'Moyen',
          detected_signals: r.local_signals,
          gemini_analysis: r.gemini || {},
          status: 'À vérifier',
          notes: `Acuité · ${art.section || ''} · ${art.date || ''}`.trim(),
        }
      });
      setSavedIds(s => new Set([...s, art.url]));
      showToast('✅ Lead créé avec analyse IA');
    } catch (e) { showToast('❌ ' + String(e)); }
    setAnalyzingId(null);
  };

  const lastScan = scans[0];
  const isRunning = lastScan?.status === 'running';

  // Couleur score article
  const scoreColor = (s) => s >= 60 ? C.green : s >= 36 ? C.accentHi : s >= 18 ? C.amber : C.muted;

  const SECTIONS = [
    { url: 'actualite/magasin',        label: 'Magasin' },
    { url: 'actualites/marche',        label: 'Marché' },
    { url: 'actualites/verres-et-lentilles', label: 'Verres & Lentilles' },
    { url: 'actualites/evenement',     label: 'Événement' },
    { url: 'actualites/santemedtech',  label: 'Santé & MedTech' },
    { url: 'actualite/profession',     label: 'Profession' },
  ];

  return (
    <div>
      {/* ── Header info ── */}
      <div style={{ ...G.card, marginBottom: 20, borderColor: '#2563eb44' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 17, color: C.text, marginBottom: 8 }}>
              📰 Surveillance Acuité.fr
            </div>
            <div style={{ fontSize: 13, color: C.textSub, lineHeight: 1.6, marginBottom: 12 }}>
              Acuité est le principal média professionnel de l'optique en France. Ce module surveille automatiquement {SECTIONS.length + 1} rubriques et détecte les publications à signal commercial pour ACEP.
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {SECTIONS.map(s => (
                <a key={s.url} href={`https://www.acuite.fr/${s.url}`} target="_blank" rel="noopener noreferrer"
                  style={{ ...G.tag(C.accent), fontSize: 11, textDecoration: 'none', cursor: 'pointer' }}>
                  {s.label}
                </a>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 160 }}>
            <button onClick={startScan} disabled={scanning || isRunning} style={{ ...G.btn, background: (scanning || isRunning) ? C.muted : C.green, whiteSpace: 'nowrap' }}>
              {scanning || isRunning ? '⏳ Scan en cours…' : '🚀 Lancer le scan complet'}
            </button>
            <button onClick={doPreview} disabled={previewing} style={{ ...G.btnGhost, whiteSpace: 'nowrap', opacity: previewing ? .6 : 1 }}>
              {previewing ? '⏳ Chargement…' : '👁 Aperçu rapide'}
            </button>
          </div>
        </div>

        {/* Progress scan en cours */}
        {(scanning || isRunning) && lastScan && (
          <div style={{ marginTop: 16, borderTop: `1px solid ${C.border}`, paddingTop: 16 }}>
            <div style={{ fontSize: 13, color: C.text, marginBottom: 8 }}>
              Analyse des rubriques : {lastScan.done_sections}/{lastScan.total_sections} · {lastScan.articles_found || 0} articles trouvés
            </div>
            <div style={{ background: C.border, borderRadius: 3, height: 6 }}>
              <div style={{ width: `${lastScan.total_sections ? (lastScan.done_sections / lastScan.total_sections) * 100 : 0}%`, height: 6, borderRadius: 3, background: C.green, transition: 'width .5s' }} />
            </div>
          </div>
        )}
      </div>

      {/* ── Historique des scans ── */}
      {scans.length > 0 && (
        <div style={{ ...G.card, marginBottom: 20 }}>
          <div style={{ fontWeight: 600, color: C.text, fontSize: 14, marginBottom: 14 }}>📋 Historique des scans</div>
          <div style={{ display: 'grid', gap: 8 }}>
            {scans.slice(0, 5).map(s => (
              <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', background: C.bg, borderRadius: 8 }}>
                <div>
                  <span style={{ fontSize: 13, color: C.text }}>{_fmtDate(s.started_at)}</span>
                  {s.status === 'done' && <span style={{ fontSize: 12, color: C.textSub, marginLeft: 12 }}>{s.total_articles} articles · {s.new_leads} nouveaux leads</span>}
                  {s.status === 'running' && <span style={{ fontSize: 12, color: C.amber, marginLeft: 12 }}>En cours…</span>}
                  {s.errors > 0 && <span style={{ fontSize: 12, color: C.red, marginLeft: 8 }}>{s.errors} erreurs</span>}
                </div>
                <span style={G.tag(s.status === 'done' ? C.green : C.amber)}>
                  {s.status === 'done' ? '✅ Terminé' : '⏳ En cours'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Aperçu rapide ── */}
      {preview && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 16, color: C.text }}>
              👁 Aperçu — {preview.total} articles récents
            </div>
            <button onClick={() => setPreview(null)} style={{ ...G.btnGhost, fontSize: 12, padding: '4px 10px' }}>✕ Fermer</button>
          </div>
          <div style={{ display: 'grid', gap: 10 }}>
            {preview.articles.map((art, i) => {
              const isSaved = savedIds.has(art.url);
              const isAnalyzing = analyzingId === art.url;
              return (
                <div key={i} style={{ ...G.card, opacity: isAnalyzing ? .5 : 1, transition: 'opacity .3s' }}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                    {/* Score */}
                    <div style={{ textAlign: 'center', minWidth: 52, flexShrink: 0 }}>
                      <div style={{ fontSize: 20, fontWeight: 700, fontFamily: 'Space Grotesk, sans-serif', color: scoreColor(art.score) }}>{art.score}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>score</div>
                    </div>

                    {/* Contenu */}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: C.text, fontSize: 14, marginBottom: 6, lineHeight: 1.4 }}>{art.title}</div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: art.excerpt ? 8 : 0 }}>
                        {art.section && <span style={{ ...G.tag(C.accent), fontSize: 11 }}>{art.section}</span>}
                        {art.date && <span style={{ fontSize: 11, color: C.muted }}>📅 {art.date}</span>}
                        {art.signals?.map(sig => (
                          <span key={sig} style={{ ...G.tag(C.green), fontSize: 10, padding: '1px 6px' }}>{sig}</span>
                        ))}
                      </div>
                      {art.excerpt && <div style={{ fontSize: 13, color: C.textSub, lineHeight: 1.5 }}>{art.excerpt}</div>}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 120, flexShrink: 0 }}>
                      {hasGemini && !isSaved && (
                        <button onClick={() => analyzeAndSave(art)} disabled={isAnalyzing || isSaved}
                          style={{ ...G.btn, background: C.purple, fontSize: 12, padding: '6px 12px', opacity: (isAnalyzing || isSaved) ? .5 : 1 }}>
                          {isAnalyzing ? '🤖…' : '🤖 Analyser + Lead'}
                        </button>
                      )}
                      {!isSaved && (
                        <button onClick={() => saveArticleAsLead(art)} disabled={isSaved}
                          style={{ ...G.btnGhost, fontSize: 12, padding: '6px 12px' }}>
                          ➕ Lead rapide
                        </button>
                      )}
                      {isSaved && <span style={{ ...G.tag(C.green), fontSize: 12, textAlign: 'center' }}>✓ Ajouté</span>}
                      <a href={art.url} target="_blank" rel="noopener noreferrer"
                        style={{ ...G.btnGhost, fontSize: 12, padding: '6px 12px', textDecoration: 'none', display: 'block', textAlign: 'center' }}>
                        🔗 Lire
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 20px', color: C.text, fontSize: 14, zIndex: 300, boxShadow: C.shadowMd }}>
          {toast}
        </div>
      )}
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────────────────────
// ── Kanban ────────────────────────────────────────────────────────────────────
const KANBAN_COLS = [
  { id: 'À vérifier',    label: 'À vérifier',    color: C.amber,   icon: '🔍' },
  { id: 'À contacter',   label: 'À contacter',   color: C.accent, icon: '📋' },
  { id: 'Contacté',      label: 'Contacté',      color: C.green,   icon: '📞' },
  { id: 'Répondu',       label: 'Répondu',       color: '#a78bfa', icon: '💬' },
  { id: 'Non pertinent', label: 'Non pertinent', color: C.muted,   icon: '✕' },
];

function KanbanCard({ lead, onDragStart, onClick }) {
  const ga = lead.gemini_analysis || {};
  const isStale = lead.days_inactive > 7 && !['Contacté','Répondu','Non pertinent'].includes(lead.status);
  const scoreColor = lead.score >= 80 ? C.green : lead.score >= 60 ? C.accentHi : lead.score >= 40 ? C.amber : C.muted;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onClick={onClick}
      style={{
        background: '#ffffff',
        border: `1px solid ${isStale ? C.amber+'55' : C.border}`,
        borderLeft: `3px solid ${PRIORITY_COLOR[lead.priority] || C.muted}`,
        borderRadius: 10,
        padding: '12px 14px',
        cursor: 'grab',
        userSelect: 'none',
        transition: 'transform .15s, box-shadow .15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px #0005'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
    >
      {/* Entreprise + score */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: C.text, lineHeight: 1.3, flex: 1, paddingRight: 8 }}>
          {lead.company || 'Inconnu'}
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: scoreColor, flexShrink: 0 }}>{lead.score}</div>
      </div>

      {/* Tags */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        <span style={{ ...G.tag(PRIORITY_COLOR[lead.priority] || C.muted), fontSize: 10, padding: '1px 7px' }}>{lead.priority}</span>
        <span style={{ ...G.tag(C.accent), fontSize: 10, padding: '1px 7px' }}>{lead.source}</span>
        {lead.country && lead.country !== 'France' && <span style={{ ...G.tag(C.muted), fontSize: 10, padding: '1px 7px' }}>{lead.country}</span>}
      </div>

      {/* Résumé IA ou texte */}
      <div style={{ fontSize: 12, color: C.textSub, lineHeight: 1.5, marginBottom: 8 }}>
        {(ga.resume || lead.publication_text || '').slice(0, 90)}{((ga.resume || lead.publication_text || '').length > 90) ? '…' : ''}
      </div>

      {/* Footer */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 11, color: C.muted }}>
        <span>{_fmtDateOnly(lead.detected_at)}</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {lead.action_count > 0 && <span style={{ color: '#a78bfa' }}>💬 {lead.action_count}</span>}
          {isStale && <span style={{ color: C.amber }}>⏰ {lead.days_inactive}j</span>}
          {lead.contact_name && <span style={{ color: C.green }}>👤</span>}
          {ga.resume && <span style={{ color: C.purple }}>🤖</span>}
        </div>
      </div>
    </div>
  );
}

function KanbanColumn({ col, leads, onDrop, onDragOver, onDragLeave, isDragOver, onCardClick }) {
  return (
    <div
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
      style={{
        background: isDragOver ? col.color + '12' : C.card,
        border: `1px solid ${isDragOver ? col.color + '88' : C.border}`,
        borderTop: `3px solid ${col.color}`,
        borderRadius: 12,
        minWidth: 220,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        transition: 'border-color .2s, background .2s',
        maxHeight: 'calc(100vh - 180px)',
      }}
    >
      {/* Header colonne */}
      <div style={{ padding: '14px 16px 10px', borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>{col.icon}</span>
          <span style={{ fontWeight: 700, fontSize: 14, color: col.color }}>{col.label}</span>
          <span style={{ marginLeft: 'auto', background: col.color + '22', color: col.color, borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 600 }}>
            {leads.length}
          </span>
        </div>
      </div>

      {/* Cartes */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {leads.length === 0 && (
          <div style={{ textAlign: 'center', color: C.muted, fontSize: 12, padding: '20px 0', opacity: isDragOver ? 1 : .5 }}>
            {isDragOver ? '⬇ Déposer ici' : 'Aucun lead'}
          </div>
        )}
        {leads.map(lead => (
          <KanbanCard
            key={lead.id}
            lead={lead}
            onDragStart={e => { e.dataTransfer.setData('leadId', lead.id); e.dataTransfer.effectAllowed = 'move'; }}
            onClick={() => onCardClick(lead)}
          />
        ))}
        {isDragOver && leads.length > 0 && (
          <div style={{ border: `2px dashed ${col.color}66`, borderRadius: 8, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: col.color }}>
            ⬇ Déposer ici
          </div>
        )}
      </div>
    </div>
  );
}

// Modal détail lead depuis Kanban
function LeadDetailModal({ lead, onClose, onUpdate }) {
  const [editStatus, setEditStatus] = useState(lead.status);
  const [editPriority, setEditPriority] = useState(lead.priority);
  const [saving, setSaving] = useState(false);
  const ga = lead.gemini_analysis || {};

  const save = async () => {
    setSaving(true);
    await onUpdate(lead.id, { status: editStatus, priority: editPriority });
    setSaving(false);
    onClose();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000b', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div style={{ ...G.card, width: '100%', maxWidth: 600, maxHeight: '85vh', overflow: 'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 17, color: C.text }}>{lead.company}</span>
          <button onClick={onClose} style={{ ...G.btnGhost, padding: '5px 10px' }}>✕</button>
        </div>

        {/* Méta */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
          <span style={G.tag(PRIORITY_COLOR[lead.priority] || C.muted)}>{lead.priority}</span>
          <span style={G.tag(C.accent)}>{lead.source}</span>
          <span style={{ fontSize: 12, color: C.muted }}>Score : {lead.score}/100</span>
          {lead.city && <span style={{ fontSize: 12, color: C.muted }}>📍 {lead.city}</span>}
        </div>

        {/* Signaux */}
        <div style={{ marginBottom: 14 }}><SignalPills signals={lead.detected_signals} /></div>

        {/* Résumé IA */}
        {ga.resume && (
          <div style={{ background: C.bg, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
            <div style={G.label}>Résumé IA</div>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{ga.resume}</div>
          </div>
        )}

        {/* Message contact */}
        {ga.message_contact && (
          <div style={{ background: C.greenBg, border: `1px solid ${C.green}33`, borderRadius: 8, padding: '12px 14px', marginBottom: 14 }}>
            <div style={{ ...G.label, color: C.green }}>💬 Message proposé</div>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{ga.message_contact}</div>
            <button onClick={() => navigator.clipboard.writeText(ga.message_contact)} style={{ ...G.btnGhost, marginTop: 8, fontSize: 12 }}>📋 Copier</button>
          </div>
        )}

        {/* Contact */}
        {lead.contact_name && (
          <div style={{ background: C.bg, borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 13 }}>
            <div style={G.label}>Contact</div>
            <div style={{ color: C.text }}>{lead.contact_name} {lead.contact_role ? `— ${lead.contact_role}` : ''}</div>
            {lead.contact_email && <div style={{ color: C.accent, marginTop: 4 }}>{lead.contact_email}</div>}
          </div>
        )}

        {/* Lien publication */}
        {lead.publication_url && (
          <div style={{ marginBottom: 14 }}>
            <a href={lead.publication_url} target="_blank" rel="noopener noreferrer" style={{ ...G.btnGhost, fontSize: 12, textDecoration: 'none', display: 'inline-block' }}>🔗 Voir la publication</a>
          </div>
        )}

        {/* Changement statut / priorité */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div><label style={G.label}>Statut</label>
            <select value={editStatus} onChange={e => setEditStatus(e.target.value)} style={{ ...G.select, width: '100%' }}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div><label style={G.label}>Priorité</label>
            <select value={editPriority} onChange={e => setEditPriority(e.target.value)} style={{ ...G.select, width: '100%' }}>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={save} disabled={saving} style={{ ...G.btn, opacity: saving ? .6 : 1 }}>{saving ? 'Sauvegarde…' : '💾 Sauvegarder'}</button>
          <button onClick={onClose} style={G.btnGhost}>Fermer</button>
        </div>
      </div>
    </div>
  );
}

function TabKanban() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dragOver, setDragOver] = useState(null);   // id colonne survolée
  const [dragId, setDragId] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [toast, setToast] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [search, setSearch] = useState('');

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 2500); };

  const load = useCallback(async () => {
    setLoading(true);
    try { setLeads(await api('/leads?sort=score&order=desc')); }
    catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDrop = async (e, colId) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('leadId');
    setDragOver(null); setDragId(null);
    if (!leadId) return;
    const lead = leads.find(l => l.id === leadId);
    if (!lead || lead.status === colId) return;

    // Optimistic update
    setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: colId } : l));
    try {
      await api(`/leads/${leadId}`, { method: 'PUT', body: { status: colId } });
      showToast(`✅ ${lead.company} → ${colId}`);
    } catch (e) {
      // Rollback
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, status: lead.status } : l));
      showToast('❌ Erreur de mise à jour');
    }
  };

  const handleUpdate = async (id, updates) => {
    await api(`/leads/${id}`, { method: 'PUT', body: updates });
    load();
  };

  // Filtres
  const visible = leads.filter(l => {
    if (filterPriority && l.priority !== filterPriority) return false;
    if (search && !l.company?.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Grouper par colonne
  const byCol = {};
  KANBAN_COLS.forEach(c => { byCol[c.id] = visible.filter(l => l.status === c.id); });
  const totalVisible = visible.length;

  if (loading) return <div style={{ textAlign: 'center', color: C.textSub, padding: 60 }}>Chargement…</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 130px)' }}>
      {/* Barre de filtres */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center', flexShrink: 0 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Filtrer par entreprise…" style={{ ...G.input, width: 200 }} />
        <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} style={G.select}>
          <option value="">Toutes priorités</option>
          {PRIORITIES.map(p => <option key={p}>{p}</option>)}
        </select>
        <span style={{ fontSize: 13, color: C.textSub }}>{totalVisible} lead{totalVisible !== 1 ? 's' : ''}</span>
        <button onClick={load} style={{ ...G.btnGhost, fontSize: 12, padding: '6px 12px', marginLeft: 'auto' }}>↻ Actualiser</button>
      </div>

      {/* Board Kanban */}
      <div style={{ display: 'flex', gap: 12, flex: 1, overflowX: 'auto', alignItems: 'flex-start', paddingBottom: 8 }}>
        {KANBAN_COLS.map(col => (
          <KanbanColumn
            key={col.id}
            col={col}
            leads={byCol[col.id] || []}
            isDragOver={dragOver === col.id}
            onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(col.id); }}
            onDrop={e => handleDrop(e, col.id)}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(null); }}
            onCardClick={lead => setSelectedLead(lead)}
          />
        ))}
      </div>

      {/* Modal détail */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={async (id, updates) => {
            await handleUpdate(id, updates);
            setSelectedLead(null);
          }}
        />
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: '12px 20px', color: C.text, fontSize: 14, zIndex: 400, boxShadow: C.shadowMd }}>
          {toast}
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [logged, setLogged] = useState(!!localStorage.getItem('ld_token'));
  const [tab, setTab] = useState('dashboard');
  const [highlightId, setHighlightId] = useState(null);
  const [hasGemini, setHasGemini] = useState(false);
  const [hasSerper, setHasSerper] = useState(false);

  const loadConfig = useCallback(async () => {
    try { const c = await api('/config'); setHasGemini(!!c.has_gemini); setHasSerper(!!c.has_serper); }
    catch (e) { if (e === 'Session invalide' || e === 'Token manquant') { localStorage.removeItem('ld_token'); setLogged(false); } }
  }, []);

  useEffect(() => { if (logged) loadConfig(); }, [logged, loadConfig]);

  if (!logged) return <Login onLogin={() => setLogged(true)} />;

  const logout = () => { api('/logout', { method: 'POST' }).catch(()=>{}); localStorage.removeItem('ld_token'); setLogged(false); };
  const handleNavigate = (targetTab, leadId) => { setTab(targetTab); if (leadId) setHighlightId(leadId); };

  const TABS = [
    { id: 'dashboard', label: '📊 Dashboard' },
    { id: 'kanban',    label: '🗂 Kanban' },
    { id: 'leads',     label: '🎯 Leads' },
    { id: 'acuite',    label: '📰 Acuité' },
    { id: 'search',    label: '🔍 Recherche' },
    { id: 'config',    label: '⚙️ Config' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: C.bg, fontFamily: 'Inter, sans-serif', color: C.text }}>
      <div style={{ background: '#ffffff', borderBottom: `1px solid ${C.border}`, padding: '0 24px', display: 'flex', alignItems: 'center', gap: 24 }}>
        <div style={{ padding: '16px 0' }}>
          <span style={{ fontFamily: 'Space Grotesk, sans-serif', fontWeight: 700, fontSize: 18, color: C.text }}>🎯 Lead Detector</span>
          <span style={{ fontSize: 12, color: C.muted, marginLeft: 10 }}>ACEP · Smart Mirror AI</span>
        </div>
        <div style={{ display: 'flex', gap: 4, flex: 1, overflowX: 'auto' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{ background: tab===t.id?C.accent+'22':'transparent', border:'none', borderBottom:`2px solid ${tab===t.id?C.accent:'transparent'}`, color: tab===t.id?C.accentHi:C.textSub, padding:'18px 16px 16px', cursor:'pointer', fontSize:13, fontWeight:tab===t.id?600:400, fontFamily:'inherit', whiteSpace:'nowrap' }}>{t.label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <GlobalSearch onNavigate={handleNavigate} />
          <span style={{ fontSize: 11, color: hasGemini?C.green:C.muted }}>{hasGemini?'● IA':'○ IA'}</span>
          <span style={{ fontSize: 11, color: hasSerper?C.green:C.muted }}>{hasSerper?'● Search':'○ Search'}</span>
          <button onClick={logout} style={{ ...G.btnGhost, fontSize: 12, padding: '5px 12px' }}>Sortir</button>
        </div>
      </div>

      <div style={{ padding: tab === 'kanban' ? '20px 24px 0' : 24, maxWidth: tab === 'kanban' ? '100%' : 1100, margin: '0 auto' }}>
        {(!hasGemini || !hasSerper) && tab !== 'config' && (
          <div style={{ background: C.amber+'15', border:`1px solid ${C.amber}44`, borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:13, color:C.amber, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
            <span>⚠️ {!hasGemini && !hasSerper ? 'Clés Gemini et Serper non configurées' : !hasGemini ? 'Clé Gemini non configurée' : 'Clé Serper non configurée'}</span>
            <button onClick={() => setTab('config')} style={{ ...G.btnGhost, fontSize:12, padding:'4px 10px', borderColor:C.amber+'66', color:C.amber }}>Configurer →</button>
          </div>
        )}
        {tab==='dashboard' && <TabDashboard />}
        {tab==='kanban'    && <TabKanban />}
        {tab==='leads'     && <TabLeads hasGemini={hasGemini} highlightId={highlightId} onClearHighlight={() => setHighlightId(null)} />}
        {tab==='acuite'    && <TabAcuite hasGemini={hasGemini} />}
        {tab==='search'    && <TabSearch hasSerper={hasSerper} />}
        {tab==='config'    && <TabConfig onConfigChange={loadConfig} />}
      </div>
    </div>
  );
}
